import { ActiveTipSequenceType, SidebarSectionType } from 'data/types';
import { getStepNumber } from 'utils/step-sidebar-helpers';

const DISMISSED_TIPS_STORAGE_KEY = 'dismissedStepTips';
const TIPS_SKIPPED_STORAGE_KEY = 'stepTipsSkipped';

export function loadDismissedTips(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_TIPS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((entry) => typeof entry === 'string')
      : [];
  } catch {
    return [];
  }
}

export function saveDismissedTips(dismissedTips: string[]): void {
  try {
    localStorage.setItem(
      DISMISSED_TIPS_STORAGE_KEY,
      JSON.stringify(dismissedTips)
    );
  } catch {
    // localStorage unavailable; tips will re-show next session
  }
}

export function loadTipsSkipped(): boolean {
  try {
    return localStorage.getItem(TIPS_SKIPPED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function saveTipsSkipped(skipped: boolean): void {
  try {
    localStorage.setItem(TIPS_SKIPPED_STORAGE_KEY, String(skipped));
  } catch {
    // localStorage unavailable; tips will re-show next session
  }
}

export function getStepCount(sections: SidebarSectionType[]): number {
  const visibleSections = sections.filter(
    (section) => section.requirementsMet !== undefined
  );

  let stepCount = 0;
  let previousGroup: string | undefined;

  for (const [index, section] of visibleSections.entries()) {
    const { progressBarGroup } = section;
    const continuesPreviousGroup =
      index > 0 &&
      progressBarGroup !== undefined &&
      progressBarGroup === previousGroup;

    if (!continuesPreviousGroup) {
      stepCount += 1;
    }

    previousGroup = progressBarGroup;
  }

  return stepCount;
}

export function getTipDismissKey(section: SidebarSectionType): string {
  return section.progressBarGroup ?? section.id;
}

export function normalizeTargets(target: string | string[]): string[] {
  return Array.isArray(target) ? target : [target];
}

export function getFallbackTipAnchorRect(): DOMRect {
  const x = window.innerWidth / 2;
  const y = window.innerHeight / 2;
  return {
    x,
    y,
    width: 0,
    height: 0,
    top: y,
    left: x,
    right: x,
    bottom: y,
    toJSON: () => ({})
  } as DOMRect;
}

export function buildStepTipSequence(
  sections: SidebarSectionType[],
  currentSectionId: string
): ActiveTipSequenceType | null {
  const section = sections.find((s) => s.id === currentSectionId);
  const step = section?.steps?.[0];

  if (!section || !step?.tips || step.tips.length === 0) {
    return null;
  }

  return {
    tips: step.tips,
    tipIndex: 0,
    dismissKey: getTipDismissKey(section),
    showWhenComplete: step.showTipsWhenComplete,
    stepNumber: getStepNumber(sections, currentSectionId),
    stepCount: getStepCount(sections),
    stepLabel: section.progressBarLabel ?? section.title
  };
}

function getFenestrationFocusTargetId(fenestrationId: string): string {
  return `fenestration-${fenestrationId}`;
}

export function buildFenestrationFocusTipSequence(
  fenestrationId: string
): ActiveTipSequenceType {
  return {
    tips: [{ targets: [getFenestrationFocusTargetId(fenestrationId)] }],
    tipIndex: 0
  };
}

export function isFenestrationFocusTipSequence(
  sequence: ActiveTipSequenceType | null,
  fenestrationId: string
): boolean {
  if (!sequence || sequence.dismissKey || sequence.tips.length !== 1) {
    return false;
  }
  const targets = sequence.tips[0]?.targets ?? [];
  return (
    targets.length === 1 &&
    targets[0] === getFenestrationFocusTargetId(fenestrationId)
  );
}
